/**
 * Dependency-free pure helpers for the client bootstrap in `main.tsx`.
 *
 * They live here (not in `main.tsx`) so the branch logic is unit-testable: importing `main.tsx`
 * pulls in the tRPC client / persister / server-fn, which isn't node-safe. `main.tsx` reads the
 * query cache / window and delegates the decisions to these.
 */

export interface TestProgressUser {
  testPassed?: boolean | null
  testPassedValues?: boolean | null
  testPassedPersonality?: boolean | null
}

/**
 * The first unfinished questionnaire (competences → values → personality), so a TEST_INCOMPLETE
 * redirect resumes where the user left off. Falls back to the first test when the user is absent.
 */
export type TestPath = '/test/competences' | '/test/values' | '/test/personality'

export function pickIncompleteTestPath(user: TestProgressUser | null | undefined): TestPath {
  if (!user?.testPassed) return '/test/competences'
  if (!user.testPassedValues) return '/test/values'
  return '/test/personality'
}

/**
 * What the client `toastErrorLink` should do for a tRPC error, decided purely from the error's
 * machine `code` (the tRPC error `message`), the transport `dataCode` (`err.data.code`), and whether
 * a Lingui translation exists for the code. `main.tsx` owns the side effects (redirect / toast).
 *
 *  - 'redirect-login'    a 401 with no live session → go to /login. INVALID_CREDENTIALS is the
 *                        exception (a failed login whose message must show in-place).
 *  - 'redirect-test'     applicant hasn't finished the test → resume the questionnaire.
 *  - 'redirect-report'   an applicant reached a company-side section that the nav hides from them
 *                        (jobs/teams → COMPANY_OR_EMPLOYEE_ONLY; employees/offers → COMPANY_ONLY) →
 *                        send them to their own profile instead of toasting a code they can't act on.
 *  - 'redirect-company'  the inverse: an employee viewing the COMPANY interface reached their own
 *                        personal report (/report → EMPLOYEE_VIEW_ONLY), which only exists in their
 *                        employee view → send them to the company home instead of crashing.
 *  - 'toast-generic'     a 500, or a BAD_REQUEST with no known code (a Zod input-validation failure
 *                        whose message is the raw issues JSON) → show a generic translated toast.
 *  - 'toast-translated'  a known machine code → render its Lingui translation.
 *  - 'toast-raw'         anything else → surface the raw message.
 */
export type TrpcErrorAction =
  | 'redirect-login'
  | 'redirect-test'
  | 'redirect-report'
  | 'redirect-company'
  | 'toast-generic'
  | 'toast-translated'
  | 'toast-raw'

export function classifyTrpcErrorToast(input: {
  code: string
  dataCode: string | undefined
  hasTranslation: boolean
}): TrpcErrorAction {
  if (input.dataCode === 'UNAUTHORIZED' && input.code !== 'INVALID_CREDENTIALS') return 'redirect-login'
  if (input.code === 'TEST_INCOMPLETE') return 'redirect-test'
  if (input.code === 'COMPANY_OR_EMPLOYEE_ONLY' || input.code === 'COMPANY_ONLY') return 'redirect-report'
  if (input.code === 'EMPLOYEE_VIEW_ONLY') return 'redirect-company'
  if (input.dataCode === 'INTERNAL_SERVER_ERROR') return 'toast-generic'
  if (input.hasTranslation) return 'toast-translated'
  // A BAD_REQUEST that didn't match a known machine code is a Zod input-validation failure: its
  // `message` is the stringified issues array (see the birthdate-format bug), useless to the user.
  // Every translatable ErrorCode that maps to BAD_REQUEST is caught by `hasTranslation` above, so
  // reaching here always means raw validation noise — show the generic toast, not the JSON dump.
  if (input.dataCode === 'BAD_REQUEST') return 'toast-generic'
  return 'toast-raw'
}
